import React, { useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import API_GATEWAY_URL from '../../config';
import { toast } from 'react-toastify';

function BlockNameInput({ label, filter_type, selectedValue, setSelectedValue }) {
    const [blockOptions, setBlockOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchBlockOptions = async (retries = 3) => {
            setIsLoading(true);
            const jwt_token = localStorage.getItem('palm_care_token');
            const apiUrl = `${API_GATEWAY_URL}/${filter_type}s`;
            const headers = { Authorization: jwt_token };
            
            for (let i = 0; i < retries; i++) {
                try {
                    const response = await axios.get(apiUrl, { headers });
                    setBlockOptions(response.data[filter_type + "s"]);
                    break; // Success, exit retry loop
                } catch (error) {
                    console.error(`Error fetching block data (Attempt ${i + 1}/${retries}):`, error);
                    if (i === retries - 1) {
                        toast.error("Failed to fetch block data after retries. Please try again.");
                    } else {
                        // Wait for 3 seconds before retrying
                        await new Promise(resolve => setTimeout(resolve, 3000));
                    }
                }
            }
            setIsLoading(false);
        };

        fetchBlockOptions();
    }, [filter_type]);

    useEffect(() => {
        const storedBlockNameRaw = localStorage.getItem('tools_selected_block_name');
        if (storedBlockNameRaw) {
            try {
                const storedObj = JSON.parse(storedBlockNameRaw);
                const blockObj = { 
                    estate: storedObj.estate, 
                    [filter_type]: storedObj.block,
                    container: storedObj.container || "01-data",
                    target_email: storedObj.target_email
                };
                
                if (!selectedValue || selectedValue.estate !== storedObj.estate || selectedValue[filter_type] !== storedObj.block) {
                    setSelectedValue(blockObj);
                    localStorage.removeItem('tools_selected_block_name');
                    
                    // Optimistically add it to options so it displays correctly before the API returns
                    setBlockOptions(prev => {
                        if (!prev.some(item => item.estate === blockObj.estate && item[filter_type] === blockObj[filter_type])) {
                            return [...prev, blockObj];
                        }
                        return prev;
                    });
                }
            } catch (e) {
                // Fallback for old plain string storage
                const blockObj = { estate: "", [filter_type]: storedBlockNameRaw };
                if (!selectedValue || selectedValue[filter_type] !== storedBlockNameRaw) {
                    setSelectedValue(blockObj);
                    localStorage.removeItem('tools_selected_block_name');
                    setBlockOptions(prev => [...prev, blockObj]);
                }
            }
        }
    }, [filter_type, selectedValue, setSelectedValue]);

    const handleAutocompleteChange = (event, newValue) => {
        let selectedDictionary = {};
        if (newValue) {
            selectedDictionary = blockOptions.find(item => `${item.estate} - ${item[filter_type]}` === newValue);
        }
        if (selectedDictionary !== selectedValue) {
            setSelectedValue(selectedDictionary);
        }
    };

    return (
        <Autocomplete
            options={blockOptions.map((item) => `${item.estate} - ${item[filter_type]}`)}
            loading={isLoading}
            renderInput={(params) => (
                <TextField 
                    {...params} 
                    label={label} 
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            autoHighlight
            value={selectedValue && selectedValue.estate && selectedValue[filter_type] ? `${selectedValue.estate} - ${selectedValue[filter_type]}` : ""}
            onChange={handleAutocompleteChange}
            style={{ width: '300px', margin: '0px 30px 20px 0px' }}
        />
    );
}

export default BlockNameInput;
